import React, { ReactElement, useEffect, useState } from 'react';
import Theme from '../components-library/Theme';
import useConfig from '../hooks/ui/useConfig';
import ColorResource from '../services/resources/ColorResource';
import { ThemeProvider as MuiThemeProvider, createTheme as createMuiTheme } from '@mui/material/styles';

interface Props {
  children: ReactElement;
}

export const ThemeContext = React.createContext(null);

export default function ThemeProvider({ children }: Props) {
  const [theme, setTheme] = useState(Theme);
  const muiTheme = createMuiTheme({
    palette: {
      primary: {
        main: theme.color.brand.primary,
      },
    },
    components: {
      MuiInputBase: {
        styleOverrides: {
          root: {
            fontSize: Theme.font.size.sm,
            lineHeight: Theme.font.lineHeight.DEFAULT,
            color: Theme.color.black.DEFAULT,
            borderColor: `${Theme.color.gray.DEFAULT}!important`,
            borderRadius: `${Theme.border.radius.lg}!important`,
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontSize: Theme.font.size.sm,
            lineHeight: Theme.font.lineHeight.DEFAULT,
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            padding: '0px!important',
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            padding: '0px!important',
          },
        },
      },
    },
  });

  const { primary_color } = useConfig();

  function createTheme() {
    updateTheme({
      ...Theme,
      color: {
        ...Theme.color,
        brand: primary_color
          ? {
              primary: primary_color,
              gradients: {
                primary: [primary_color, primary_color],
              },
            }
          : Theme.color.brand,
      },
    });
  }

  function updateTheme(newTheme) {
    document.documentElement.style.setProperty(
      '--company-color-primary',
      ColorResource.getRgbValue(newTheme.color.brand.primary),
    );
    document.documentElement.style.setProperty(
      '--company-color-gradient-primary',
      ColorResource.getRgbValue(newTheme.color.brand.gradients?.primary[0]),
    );
    document.documentElement.style.setProperty(
      '--company-color-gradient-secondary',
      ColorResource.getRgbValue(newTheme.color.brand.gradients?.primary[1]),
    );

    setTheme(newTheme);
  }

  useEffect(() => {
    createTheme();
  }, [primary_color]);

  return (
    <ThemeContext.Provider value={theme}>
      <MuiThemeProvider theme={muiTheme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
}
